// src/utils/teamAccessWriters.js
//
// Pass 50A §T — access/invite writers. EMULATOR / test only (no production invites/Auth in this
// pass). CRITICAL identity rule (Pass 49A binding): inviting an EXISTING canonical member attaches
// the invitation/Auth to that SAME member doc — it NEVER creates a second organizationMember.
// The only writer that creates a member is addPerson (new person, deterministic id).

import { addDoc, updateDoc, doc, collection, serverTimestamp } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { memberDocId, permissionRoleOf } from './teamAccess';
import { assertCanManageTeamAccess } from './operationalWriteGate';
import { canAdministerOwner } from './platformAccess';

// Pass 58B — any transition that TOUCHES the owner role (promoting TO owner, or changing/disabling a
// member who IS the owner) requires an owner/super actor. A plain org admin may manage Admin/Ops/
// Instructor but must never create, demote, disable, or revoke the owner. Fails closed.
const OWNER_ROLE = 'organization_owner';
function assertOwnerTransition(ctx, member, nextRole) {
  const involvesOwner = permissionRoleOf(member) === OWNER_ROLE || nextRole === OWNER_ROLE;
  if (!involvesOwner) return;
  const orgId = (ctx && ctx.organizationId) || member?.organizationId || '';
  if (!ctx || !ctx.userData || !canAdministerOwner(ctx.userData, orgId)) {
    const e = new Error('Only an organization owner or platform super-admin may administer the owner role.');
    e.code = 'OWNER_TRANSITION_DENIED';
    throw e;
  }
}

const MEMBERS = PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS;
const INVITES = PLATFORM_COLLECTIONS.ORGANIZATION_INVITES || 'organizationInvites';

const requireMemberId = (member) => {
  const id = memberDocId(member);
  if (!id) throw new Error('Cannot resolve the canonical member document id.');
  return id;
};

// §11 add a NEW person to the roster (before any Auth). Deterministic id → re-adding is a no-op
// upsert, never a duplicate. Returns the member id.
// §18 default permission role is a NEUTRAL 'member' — a driver/concierge/admin person must not
// become an instructor permission role merely because they are operational staff. Teaching
// CAPABILITY (separate axis) is what determines lesson eligibility.
export async function addPerson(db, { organizationId, userId, name, role = 'member', jobFunction = '', seasonId, participation, category } = {}) {
  assertCanManageTeamAccess();
  if (!organizationId || !userId) throw new Error('organizationId and userId are required.');
  const id = `${userId}::${organizationId}`;
  const seasonMemberships = seasonId && (participation || category)
    ? { [seasonId]: { ...(participation ? { participation } : {}), ...(category ? { category } : {}) } } : undefined;
  await setDocLike(db, id, {
    organizationId, userId, name, role, status: 'active',
    ...(jobFunction ? { jobFunction } : {}),
    ...(seasonMemberships ? { seasonMemberships } : {}),
    createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return id;
}

// Pass 58A: every mutating writer now accepts an ACTOR context `{ userData, organizationId, actorUserId,
// actorName }`. When `userData` is supplied the guard authorizes by REAL role (owner/admin only, via
// operationalWriteGate) and the write records the actor (updatedBy/updatedByName). With no context the
// legacy activation-flag guard applies (back-compat for existing tests/callers). No new writer is
// introduced — this only wires actor + role authorization into the existing safe writers.
const ctxAuth = (ctx, member) => (ctx && ctx.userData
  ? { userData: ctx.userData, organizationId: ctx.organizationId || member?.organizationId || '' }
  : null);
const actorStamp = (ctx) => ({ updatedBy: (ctx && ctx.actorUserId) || '', updatedByName: (ctx && ctx.actorName) || '' });

// §T invite an EXISTING canonical member to Motus — attaches invite state to that member + a
// linked invite record. NEVER creates a new organizationMember (no addDoc to MEMBERS here).
export async function invitePersonToMotus(db, member, { email = '', permissionRole = 'instructor', invitedByUserId = '', userData = null, organizationId = '', actorName = '' } = {}) {
  assertCanManageTeamAccess(userData ? { userData, organizationId: organizationId || member?.organizationId || '' } : null);
  const memberId = requireMemberId(member);
  await updateDoc(doc(db, MEMBERS, memberId), {
    inviteStatus: 'pending', invitedAt: serverTimestamp(), invitedByUserId,
    invitePermissionRole: permissionRole,
    ...(email ? { email } : {}),
    updatedBy: invitedByUserId || '', updatedByName: actorName || '',
    updatedAt: serverTimestamp(),
  });
  // linked invite record references the SAME member so acceptance attaches authUserId to it.
  await addDoc(collection(db, INVITES), {
    organizationId: member.organizationId, targetMemberId: memberId, email, permissionRole,
    status: 'pending', invitedByUserId, createdAt: serverTimestamp(),
  });
  return { memberId };
}

export async function resendInvite(db, member, ctx = {}) {
  assertCanManageTeamAccess(ctxAuth(ctx, member));
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { inviteResentAt: serverTimestamp(), ...actorStamp(ctx), updatedAt: serverTimestamp() });
}
export async function cancelInvite(db, member, ctx = {}) {
  assertCanManageTeamAccess(ctxAuth(ctx, member));
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { inviteStatus: '', invitedAt: null, ...actorStamp(ctx), updatedAt: serverTimestamp() });
}
export async function disableAccess(db, member, ctx = {}) {
  assertCanManageTeamAccess(ctxAuth(ctx, member));
  assertOwnerTransition(ctx, member, null); // disabling the owner requires an owner/super actor
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { accessDisabled: true, inviteStatus: 'disabled', ...actorStamp(ctx), updatedAt: serverTimestamp() });
}
export async function enableAccess(db, member, ctx = {}) {
  assertCanManageTeamAccess(ctxAuth(ctx, member));
  assertOwnerTransition(ctx, member, null);
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { accessDisabled: false, inviteStatus: member.authUserId ? '' : 'pending', ...actorStamp(ctx), updatedAt: serverTimestamp() });
}
// Role mutation stays ORG-SCOPED and preserves member.userId + authUserId (writes only orgRole). Records
// actor. Owner-involving transitions require an owner/super actor (assertOwnerTransition).
export async function changePermissionRole(db, member, orgRole, ctx = {}) {
  assertCanManageTeamAccess(ctxAuth(ctx, member));
  assertOwnerTransition(ctx, member, orgRole);
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { orgRole, ...actorStamp(ctx), updatedAt: serverTimestamp() });
}
export async function updateCapabilities(db, member, seasonId, workCapabilities) {
  assertCanManageTeamAccess();
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { [`seasonMemberships.${seasonId}.workCapabilities`]: workCapabilities, updatedAt: serverTimestamp() });
}
export async function updateSeasonParticipation(db, member, seasonId, participation) {
  assertCanManageTeamAccess();
  await updateDoc(doc(db, MEMBERS, requireMemberId(member)), { [`seasonMemberships.${seasonId}.participation`]: participation, updatedAt: serverTimestamp() });
}

// setDoc-with-merge shim (kept out of the import list to make the no-addDoc-to-MEMBERS invariant
// obvious in the invite path).
async function setDocLike(db, id, data) {
  const { setDoc } = await import('firebase/firestore');
  await setDoc(doc(db, MEMBERS, id), data, { merge: true });
}
